import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Grid, IconButton, Breadcrumbs, Link,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Menu, MenuItem,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Material Icons
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close'; // <-- NEW: For removing items from the upload list

// Auth & Wrappers
import { useAuth0 } from '@auth0/auth0-react';
import { wrapper } from 'utils/wrapper';
import api from 'services/api/campaign';

// Soft UI Components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftSelect from "components/SoftComponents/SoftSelect";
import { useSoftUIController } from 'hooks/context';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import theme from 'assets/theme';

import { ProgressBar } from "react-bootstrap";
import { disableMap } from 'utils/map';

// Muted Thumbnail Component
const ContentItemThumbnail = ({ item, isModal, setDataUrl, selectedOrg }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['imagePreview', item._id, selectedOrg],
    queryFn: () => api.getContentFile(item._id),
    enabled: item.type === 'image',
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (item.type === 'image' && data?.signedUrl) {
      setDataUrl(data.signedUrl);
    }
  }, [data?.signedUrl, item.type, setDataUrl]);

  if (item.type === 'folder') return <FolderIcon sx={{ fontSize: '72px !important' }} color="secondary" />;
  if (item.type === 'form') return <DescriptionIcon sx={{ fontSize: '72px !important' }} color="text" />;

  if (item.type === 'image') {
    if (isLoading) return <CircularProgress size={24} color="inherit" sx={{ m: 1.5 }} />;
    if (isError || !data?.signedUrl) return <ImageIcon sx={{ fontSize: '72px !important' }} color="info" />;

    return (
      <SoftBox
        component="img"
        src={data.signedUrl}
        alt={item.name}
        borderRadius="md"
        shadow="sm"
        sx={{ width: 72, height: 72, objectFit: 'cover', cursor: 'pointer' }}
      />
    );

  }
  return <DescriptionIcon sx={{ fontSize: '72px !important' }} color="text" />;
};

const ContentItemCard = ({ item, isModal, appearance, selectedOrg, onSelect, handleNavigateToFolder, handleMenuOpen, canMediaEdit, canMediaDelete }) => {
  // Now the hook is at the top level of its own component
  const [dataUrl, setDataUrl] = useState(null);

  const itemCard = (
    <Card
      sx={({ palette, functions: { rgba } }) => ({
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' },
        ...(onSelect && item.type === 'image' && { border: '1px solid transparent', '&:hover': { borderColor: '#ddd' } }),
        background: rgba(palette[appearance].card, 1)
      })}
      onClick={() => {
        if (item.type === 'folder') handleNavigateToFolder(item._id, item.name);
        else if (onSelect && item.type === 'image') onSelect(item);
      }}
      title={item.name}
    >
      <SoftBox p={2} display="flex" alignItems="center">
        <SoftBox mr={2} display="flex" alignItems="center" justifyContent="center" minWidth="48px">
          {/* Note: Pass setDataUrl down to the thumbnail component */}
          {(item.type === 'image' && dataUrl != null && isModal) ? (
            <PhotoView key={dataUrl} src={dataUrl}>
              <ContentItemThumbnail {...{ item, isModal, setDataUrl, selectedOrg }} />
            </PhotoView>
          ) : (
            <ContentItemThumbnail {...{ item, isModal, setDataUrl, selectedOrg }} />
          )}
        </SoftBox>
        <SoftBox flexGrow={1} overflow="hidden">
          <SoftTypography variant="subtitle2" noWrap fontWeight="bold">{item.name}</SoftTypography>
          <SoftTypography variant="caption" color="text">{item.type.toUpperCase()}</SoftTypography>
        </SoftBox>
        {(canMediaEdit || canMediaDelete) && (
          <SoftBox>
            <IconButton size="medium" onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleMenuOpen(e, item);
            }}
              sx={({ palette, functions: { rgba } }) => ({
                color: rgba(palette[appearance].text, 1)
              })}>
              <MoreVertIcon />
            </IconButton>
          </SoftBox>
        )}
      </SoftBox>
    </Card>
  );

  return (
    <Grid item xxs={12} md={6} lg={4} xl={3}>
      {(item.type === 'image' && dataUrl != null) ? (
        <PhotoProvider maskOpacity={0.8}>
          {!isModal ? (
            <PhotoView key={dataUrl} src={dataUrl}>
              {itemCard}
            </PhotoView>
          ) : (
            itemCard
          )}
        </PhotoProvider>
      ) : (
        itemCard
      )}
    </Grid >
  );
};

function ContentRepositoryView({ map, user, onSelect, isModal = false }) {
  const [controller] = useSoftUIController();
  const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

  useEffect(() => {
    if (!map?.current) return;
    disableMap(map, user, selectedOrg);
  }, [selectedOrg]);

  const queryClient = useQueryClient();
  api.loadToken(useAuth0());

  const canMediaAdd = userPermissions.indexOf('media:add') >= 0;
  const canMediaEdit = userPermissions.indexOf('media:edit') >= 0;
  const canMediaDelete = userPermissions.indexOf('media:delete') >= 0;

  // State
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Root' }]);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [newFolderName, setNewFolderName] = useState('');

  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [destinationFolderId, setDestinationFolderId] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;

  // Queries
  const { data: allItems = [], isLoading, isError } = useQuery({
    queryKey: ['contentTree', selectedOrg],
    queryFn: () => api.getContentTree(),
  });

  const currentFolderItems = allItems.filter(item => item.parentId === currentFolderId);
  const allFolders = allItems.filter(item => item.type === 'folder');

  // Mutations
  const createFolderMutation = useMutation({
    mutationFn: ({ name, parentId }) => api.createFolder(name, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentTree', selectedOrg] });
      setFolderModalOpen(false);
      setNewFolderName('');
    },
  });

  // <-- NEW: Loop over the array of files and upload them in parallel
  const uploadFilesMutation = useMutation({
    mutationFn: async ({ files, parentId }) => {
      setUploadProgress(0);

      const totalBytesArray = files.map(file => file.size);
      const uploadedBytesArray = files.map(() => 0);
      const absoluteTotalBytes = totalBytesArray.reduce((a, b) => a + b, 0);

      const uploadPromises = files.map((file, index) => {
        return api.uploadImage(file, parentId, {
          onUploadProgress: (progressEvent) => {
            uploadedBytesArray[index] = progressEvent.loaded;

            const absoluteUploadedBytes = uploadedBytesArray.reduce((a, b) => a + b, 0);
            const totalPercent = Math.round((absoluteUploadedBytes * 100) / absoluteTotalBytes);

            setUploadProgress(totalPercent > 100 ? 100 : totalPercent);
          }
        });
      });

      return Promise.all(uploadPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentTree', selectedOrg] });
      setUploadModalOpen(false);
      setUploadFiles([]); // Clear the array on success
      setUploadProgress(0);
    },
    onError: (error) => {
      // Keep dialog open on failure but reset progress view metrics
      setUploadProgress(0);
      console.error("Upload error details:", error);
    }
  });

  const moveItemMutation = useMutation({
    mutationFn: ({ contentId, newParentId }) => api.moveContent(contentId, newParentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentTree', selectedOrg] });
      setMoveModalOpen(false);
      handleMenuClose();
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id) => api.deleteContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentTree', selectedOrg] });
      setDeleteModalOpen(false);
      handleMenuClose();
    }
  });

  // Handlers
  const handleNavigateToFolder = (folderId, folderName) => {
    setBreadcrumbs([...breadcrumbs, { id: folderId, name: folderName }]);
  };

  const handleBreadcrumbClick = (index) => {
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    createFolderMutation.mutate({ name: newFolderName, parentId: currentFolderId });
  };

  // <-- NEW: Handlers for multiple file selection
  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;
    // Append new files to any already selected files
    setUploadFiles(prev => [...prev, ...newFiles]);
    // Reset the input so the user can select the same file again if they deleted it
    e.target.value = null;
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleUploadFiles = () => {
    if (uploadFiles.length === 0) return;
    uploadFilesMutation.mutate({ files: uploadFiles, parentId: currentFolderId });
  };

  const handleMenuOpen = (event, item) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      const buttonElement = event.currentTarget;

      setSelectedItem(item);
      setAnchorEl(buttonElement);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => setSelectedItem(null), 300);
  };

  const handleOpenMoveModal = () => {
    setDestinationFolderId('');
    setMoveModalOpen(true);
    setAnchorEl(null);
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
    setAnchorEl(null);
  };

  const executeMove = () => {
    const parentIdToSubmit = destinationFolderId === '' ? null : destinationFolderId;
    moveItemMutation.mutate({ contentId: selectedItem._id, newParentId: parentIdToSubmit });
  };

  const executeDelete = () => {
    if (!selectedItem) return;
    deleteItemMutation.mutate(selectedItem._id);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadFiles([]);
  };

  // Options
  const moveFolderOptions = [
    { value: "", label: "Root Directory" },
    ...allFolders
      .filter(f => f._id !== selectedItem?._id)
      .map((f) => ({ value: f._id, label: f.name }))
  ];

  const StyledProgressBar = styled(ProgressBar)({
    borderRadius: '16px',
    '& .progress-bar': {
      backgroundImage: `linear-gradient(310deg, #2152ff, #21d4fd) !important`,
      borderRadius: '16px',
      fontFamily: 'Roboto,Helvetica,Arial,sans-serif',
      paddingLeft: '8px',
      height: '20px',
      width: `calc(${uploadProgress}% - 8px) !important`
    },
  });

  // Core UI
  const viewContent = (
    <SoftBox py={isModal ? 2 : 0} px={isModal ? 2 : 1} width={isModal ? "calc(100% - 32px)" : "calc(100% - 16px)"} height="auto">
      <Card sx={({ palette, functions: { rgba } }) => ({ backgroundColor: isModal ? 'transparent' : rgba(palette[appearance].content, 1), height: '100%' })}>
        <SoftBox py={1} px={deviceLayout == 'mobile' ? 1 : 2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>

          {/* {isModal && (
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <SoftTypography variant={isModal ? "h5" : "h4"} fontWeight="bold">
              Content Repository
            </SoftTypography>
            {canMediaAdd && (
              <SoftBox display="flex" gap={2}>
                <SoftButton
                  variant="outlined"
                  color={appearance}
                  startIcon={<AddIcon />}
                  onClick={() => setFolderModalOpen(true)}
                >
                  New Folder
                </SoftButton>
                <SoftButton
                  variant="contained"
                  color="secondary"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setUploadModalOpen(true)}
                >
                  Upload Files
                </SoftButton>
              </SoftBox>
            )}
          </SoftBox>
        )} */}
          <Box display="flex" justifyContent={'space-between'} alignItems={'center'} sx={{ gap: 1 }}>
            <Breadcrumbs sx={({ palette, functions: { rgba } }) => ({ color: rgba(palette[appearance].text, 1), '.MuiBreadcrumbs-separator': { color: rgba(palette[appearance].text, 1) } })}>
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <SoftTypography key={index} variant="button" fontWeight="bold">
                    {crumb.name}
                  </SoftTypography>
                ) : (
                  <Link
                    key={index} underline="hover" color="inherit"
                    sx={{ cursor: 'pointer' }} onClick={() => handleBreadcrumbClick(index)}
                  >
                    <SoftTypography variant="button" fontWeight="regular">
                      {crumb.name}
                    </SoftTypography>
                  </Link>
                );
              })}
            </Breadcrumbs>
            <SoftBox display='flex' flexDirection={{ xxs: 'column', sm: 'row' }} justifyContent={{ xxs: 'center', xs: 'flex-end' }} gap={1}>
              <SoftButton
                variant="outlined"
                color={appearance}
                startIcon={<AddIcon />}
                onClick={() => setFolderModalOpen(true)}
                size={deviceLayout == 'mobile' ? 'small' : 'medium'}
              >
                {deviceLayout == 'mobile' ? 'New' : 'New Folder'}
              </SoftButton>
              <SoftButton
                variant="contained"
                color="secondary"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadModalOpen(true)}
                size={deviceLayout == 'mobile' ? 'small' : 'medium'}
              >
                {deviceLayout == 'mobile' ? 'Upload' : 'Upload Files'}
              </SoftButton>
            </SoftBox>
          </Box>
        </SoftBox>

        {isLoading && (
          <SoftBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="inherit" />
          </SoftBox>
        )}

        {isError && (
          <SoftTypography variant="button" color="error" fontWeight="medium" mb={2}>
            Failed to load content. Please try again.
          </SoftTypography>
        )}



        {!isLoading && !isError && (
          <SoftBox sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
            <Grid container spacing={2}>
              {currentFolderItems.length === 0 && (
                <Grid item xxs={12}>
                  <SoftTypography variant="button" color="text" fontStyle="italic">
                    This folder is empty.
                  </SoftTypography>
                </Grid>
              )}
              {currentFolderItems.map((item) => (
                <ContentItemCard key={item._id} {...{ item, isModal, appearance, selectedOrg, onSelect, handleNavigateToFolder, handleMenuOpen, canMediaEdit, canMediaDelete }} />
              ))}
            </Grid>
          </SoftBox>
        )}

        {(canMediaEdit || canMediaDelete) && (
          <>
            {/* Context Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} disablePortal={false} anchorReference='anchorEl'
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { borderRadius: '0.75rem', minWidth: '150px', p: 1 } }}
            >
              <MenuItem onClick={handleOpenMoveModal} sx={{ borderRadius: '0.5rem' }}>
                <DriveFileMoveIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> Move Item
              </MenuItem>
              <MenuItem onClick={handleOpenDeleteModal} sx={{ borderRadius: '0.5rem', color: 'error.main' }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
              </MenuItem>
            </Menu>
          </>
        )}

        {canMediaEdit && (
          <>
            {/* Move Item Dialog */}
            <Dialog
              open={moveModalOpen}
              onClose={() => setMoveModalOpen(false)}
              PaperProps={{
                sx: {
                  borderRadius: '1rem',
                  minWidth: '60%',
                  overflow: 'visible'
                }
              }}
            >
              <DialogTitle>
                <SoftTypography variant="h6" fontWeight="bold">
                  Move "{selectedItem?.name}"
                </SoftTypography>
              </DialogTitle>

              <DialogContent sx={{ overflow: 'visible', display: 'block' }}>
                <SoftBox mt={1} sx={{ overflow: 'visible' }}>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">
                    Destination Folder
                  </SoftTypography>

                  <SoftSelect
                    options={moveFolderOptions}
                    value={moveFolderOptions.find(opt => opt.value === destinationFolderId) || moveFolderOptions[0]}
                    onChange={(selected) => setDestinationFolderId(selected.value)}
                    isDisabled={moveItemMutation.isPending}
                  />
                </SoftBox>
              </DialogContent>

              <DialogActions sx={{ p: 2, mt: 2 }}>
                <SoftButton onClick={() => setMoveModalOpen(false)} variant="text" color="secondary" disabled={moveItemMutation.isPending}>
                  Cancel
                </SoftButton>
                <SoftButton onClick={executeMove} variant="gradient" color={appearance} disabled={moveItemMutation.isPending}>
                  {moveItemMutation.isPending ? 'Moving...' : 'Move Here'}
                </SoftButton>
              </DialogActions>
            </Dialog>
          </>
        )}

        {canMediaDelete && (
          <>
            {/* Delete Item Dialog */}
            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ sx: { borderRadius: '1rem', minWidth: '60%' } }}>
              <DialogTitle>
                <SoftTypography variant="h6" fontWeight="bold" color="error">Confirm Deletion</SoftTypography>
              </DialogTitle>
              <DialogContent>
                <SoftBox mt={1}>
                  {selectedItem?.type === 'folder' ? (
                    <>
                      <SoftTypography variant="body2" color="error" fontWeight="bold" display="block">
                        WARNING:
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        This is a folder. Deleting it will also delete all files and sub-folders contained inside <strong>"{selectedItem?.name}"</strong>.
                      </SoftTypography>
                    </>
                  ) : (
                    <SoftTypography variant="body2" color="text">
                      Are you sure you want to delete <strong>"{selectedItem?.name}"</strong>? This action cannot be undone.
                    </SoftTypography>
                  )}
                </SoftBox>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <SoftButton
                  onClick={() => setDeleteModalOpen(false)}
                  variant="text"
                  color="secondary"
                  disabled={deleteItemMutation.isPending}
                >
                  Cancel
                </SoftButton>
                <SoftButton
                  onClick={executeDelete}
                  variant="gradient"
                  color="error"
                  disabled={deleteItemMutation.isPending}
                >
                  {deleteItemMutation.isPending ? 'Deleting...' : 'Delete'}
                </SoftButton>
              </DialogActions>
            </Dialog>
          </>
        )}

        {canMediaAdd && (
          <>
            {/* Create Folder Dialog */}
            <Dialog open={folderModalOpen} onClose={() => setFolderModalOpen(false)} PaperProps={{ sx: { borderRadius: '1rem', minWidth: '60%' } }}>
              <DialogTitle><SoftTypography variant="h6" fontWeight="bold">Create New Folder</SoftTypography></DialogTitle>
              <DialogContent>
                <SoftBox mt={1}>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Folder Name</SoftTypography>
                  <SoftInput
                    autoFocus
                    placeholder="e.g. Summer Campaign"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    disabled={createFolderMutation.isPending}
                  />
                </SoftBox>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <SoftButton onClick={() => setFolderModalOpen(false)} variant="text" color="secondary" disabled={createFolderMutation.isPending}>Cancel</SoftButton>
                <SoftButton onClick={handleCreateFolder} variant="gradient" color={appearance} disabled={createFolderMutation.isPending}>
                  {createFolderMutation.isPending ? 'Creating...' : 'Create'}
                </SoftButton>
              </DialogActions>
            </Dialog>

            {/* Upload Files Dialog */}
            <Dialog open={uploadModalOpen} onClose={closeUploadModal} PaperProps={{ sx: { borderRadius: '1rem', minWidth: '60%' } }}>
              <DialogTitle><SoftTypography variant="h6" fontWeight="bold">Upload Files</SoftTypography></DialogTitle>
              <DialogContent>
                {uploadFilesMutation.isPending && (
                  <SoftBox mb={3} mt={1} mx={2}>
                    <StyledProgressBar
                      now={uploadProgress}
                      label={`${uploadProgress}%`}
                      animated
                    />
                  </SoftBox>
                )}
                <SoftBox mt={1} mx={2}>

                  {/* <-- NEW: List of selected files with remove buttons */}
                  {uploadFiles.length > 0 && (
                    <SoftBox mb={2} maxHeight="200px" sx={{ overflowY: 'auto' }}>
                      {uploadFiles.map((file, index) => (
                        <SoftBox
                          key={`${file.name}-${index}`}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          p={1.5}
                          mb={1}
                          sx={{ backgroundColor: '#f8f9fa', borderRadius: '0.5rem', border: '1px solid #e9ecef' }}
                        >
                          <SoftTypography variant="caption" fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }}>
                            {file.name}
                          </SoftTypography>
                          <IconButton size="small" onClick={() => handleRemoveFile(index)} disabled={uploadFilesMutation.isPending} sx={{ p: 0.5 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}

                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    component="label"
                    fullWidth
                    sx={{ py: uploadFiles.length > 0 ? 2 : 4, borderStyle: 'dashed' }}
                    disabled={uploadFilesMutation.isPending}
                  >
                    {/* Note the "multiple" attribute added to the input tag below */}
                    {uploadFiles.length > 0 ? '+ Add More Files' : 'Click to browse files'}
                    <input type="file" hidden multiple accept="image/*" onChange={handleFileSelect} />
                  </SoftButton>

                </SoftBox>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <SoftButton onClick={closeUploadModal} variant="text" color="secondary" disabled={uploadFilesMutation.isPending}>Cancel</SoftButton>
                <SoftButton onClick={handleUploadFiles} variant="gradient" color={appearance} disabled={uploadFiles.length === 0 || uploadFilesMutation.isPending}>
                  {uploadFilesMutation.isPending
                    ? 'Uploading...'
                    : `Upload ${uploadFiles.length > 0 ? uploadFiles.length : ''} File${uploadFiles.length > 1 ? 's' : ''}`
                  }
                </SoftButton>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Card>
    </SoftBox>
  );

  // return isModal ? viewContent : wrapper(viewContent);

  return isModal ? viewContent : wrapper((
    <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
      sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
    >
      <SoftBox mt={1} display="flex" sx={{ height: `calc(100% - 16px)` }}>
        {viewContent}
      </SoftBox>
    </SoftBox>
  ), controller);
}



export default ContentRepositoryView;